import type { AxiosError } from 'axios'
import api from './api'
import { formatApiError, getApiErrorDetail } from '@/utils/apiError'
import type {
  CaseInputs as EngineCaseInputs,
  CashPolicy,
  CashPolicyCadence,
  CashPolicySplitSchedule,
  CashPolicyYieldView,
  DriverParams,
  DriverType,
  Model as EngineModel,
  Recipe,
  RecipeOverride,
  View,
  WaterfallMode,
} from '@/pages/projections/engine/types'
import { parseNumeric } from '@/pages/projections/engine/schema'
import { migrateTotalOrdersDriversToAvgOrders } from '@/pages/projections/migrateTotalOrdersDrivers'
import { normaliseRecipe } from '@/pages/projections/caseJson/recipe'

export type { View }

export type ApiDriverTypeCache = {
  values: (string | null)[]
  params: Record<string, unknown>
  recipe?: unknown
}

export type ApiDriverInputs = {
  active: string | null
  by_type: Record<string, ApiDriverTypeCache>
}

export type ApiOneTimeItem = {
  id: string
  month: string
  view: View
  line_key: string
  amount: string
  mode: 'incremental' | 'already_in_driver'
  notes?: string | null
}

export type ApiCashPolicySplitScheduleBasis = {
  anchor_date: string
  cadence: CashPolicyCadence
  amount_per_period: string
  horizon_months: number
  last_dist_num: number
  last_closed_month: string | null
  mode: WaterfallMode
  one_time_date?: string | null
  one_time_amount?: string | null
}

export type ApiCashPolicySplitSchedulePeriod = {
  date: string
  t: number
  tiers: Record<string, string>
}

export type ApiCashPolicySplitScheduleMonth = {
  t: number
  lines: Record<string, string>
}

export type ApiCashPolicySplitSchedule = {
  basis: ApiCashPolicySplitScheduleBasis
  computed_at: string
  months: ApiCashPolicySplitScheduleMonth[]
  periods: ApiCashPolicySplitSchedulePeriod[]
  pref_accrual?: ApiCashPolicySplitScheduleMonth[] | null
}

export type ApiCashPolicy = {
  enabled: boolean
  floor: {
    operating_minimum: string | null
    drawdown_allowance: string | null
  }
  distribution: {
    cadence: CashPolicyCadence
    anchor_date: string
    amount_per_period: string | null
    one_time?: { date: string; amount: string } | null
    split_schedule: ApiCashPolicySplitSchedule | null
  }
  sweep: {
    enabled: boolean
    mode?: 'excess' | 'fixed'
    amount_per_period?: string | null
    yield_snapshot: {
      as_of: string
      source: string
      spread: string
      apr_by_month: string[]
    } | null
    yield_income_view: CashPolicyYieldView | null
    opening_mm_balance: string | null
  }
  solver: {
    horizon_months: 12 | 24 | 36
    round_to: 500 | 1000 | 5000
  }
}

export type ApiCaseInputs = {
  consolidated_only: string[]
  drivers: Record<string, Record<string, ApiDriverInputs>>
  one_time_items: ApiOneTimeItem[]
  cash_policy?: ApiCashPolicy | null
}

export type ValidationItem = {
  code: string
  line_key: string | null
  view: string | null
  t: number | null
  message: string
  label?: string | null
  section?: string | null
}

export type RollForwardFailure = {
  case_id: number
  case_name: string
  error: string
}

export type RollForwardSummary = {
  k_months: number
  cases_processed: number
  cases_skipped: number
  snapshots_written: number
  one_time_items_dropped: number
  recipe_overrides_dropped: number
  recipe_path_segments_dropped: number
  failures: RollForwardFailure[]
  cash_policy_one_time_cleared: Array<{
    case_id: number
    case_name: string
    date: string
    amount: unknown
    dist_num: number | null
  }>
  cash_policy_schedules_recomputed: number
  cash_policy_schedule_failures: Array<{
    case_id: number
    case_name: string
    error: string
  }>
}

export type CaseListItem = {
  id: number
  name: string
  description: string | null
  expires_on: string | null
  expired: boolean
  saved_at: string | null
  kpis: Record<string, unknown>
  cash_policy_summary: Record<string, unknown> | null
  updated_at: string
  outputs_stale: boolean
  rolled_forward_at: string | null
  rolled_forward_from_import_id: number | null
  rolled_forward_months: number | null
  snapshot_months: string[]
}

export type CaseDetail = {
  id: number
  name: string
  description: string | null
  expires_on: string | null
  expired: boolean
  base_import_id: number
  inputs: ApiCaseInputs
  outputs: Record<string, Record<string, (string | null)[]>>
  kpis: Record<string, unknown>
  validation: Record<string, unknown> | null
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
  saved_at: string | null
  outputs_stale: boolean
  rolled_forward_at: string | null
  rolled_forward_from_import_id: number | null
  rolled_forward_months: number | null
  snapshot_months: string[]
  pre_rollforward_outputs: Record<string, Record<string, (string | null)[]>> | null
}

export type ModelSummaryResponse = {
  latest_import: {
    id: number
    last_closed_month: string
    imported_at: string
    warning_count: number
    control_status: string
    n_months: number
    last_col: string
    roll_forward_pending_case_ids: number[]
    import_revertible?: boolean
  } | null
  schema_fingerprint: string
  settings: Record<string, unknown>
  case_count: number
  partner_labels: Record<string, string> | null
  last_dist_num: number | null
  last_dist_date: string | null
  logged_after_close: Array<{
    num: number
    date: string
    amount: string
    lines: Record<string, string> | null
    warning?: string | null
  }>
  tier4_shares: Record<string, string> | null
  tier4_warnings: string[]
}

export type ImportResultResponse = {
  id: number
  n_months: number
  last_col: string
  last_closed_month: string
  control_status: string
  warning_count: number
  imported_at: string
  labor_months?: number | null
  labor_warning_count?: number | null
  labor_error?: string | null
  roll_forward: RollForwardSummary | null
}

export type LaborHistoryImportResponse = {
  months: number
  warning_count: number
  warnings: Array<Record<string, unknown>>
  first_month: string
}

export type CaseCreateBody = {
  name: string
  from_case_id?: number
  description?: string
  expires_on?: string
}

export type CaseUpdateBody = {
  name: string
  description?: string | null
  expires_on?: string | null
  inputs: ApiCaseInputs
  outputs: Record<string, Record<string, (string | null)[]>>
  kpis: Record<string, unknown>
  expected_updated_at: string
  acknowledge_warnings?: boolean
}

export type CgmHistoryTab = {
  dates: string[]
  rows: Record<string, { values: (string | number | null)[] }>
}

/** GET /history returns the import payload keyed by CGM tab name. */
export type CgmHistoryPayload = Record<string, CgmHistoryTab> & {
  schema_stale?: boolean
}

function parseApiNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = parseNumeric(value)
  return typeof parsed === 'number' ? parsed : null
}

function parseApiParams(params: Record<string, unknown> | undefined): DriverParams | undefined {
  if (!params || Object.keys(params).length === 0) {
    return undefined
  }
  const result: DriverParams = {}
  if (params.ref != null && params.ref !== '') {
    result.ref = String(params.ref)
  }
  for (const key of ['start', 'end', 'midpoint', 'steepness'] as const) {
    if (params[key] != null) {
      const parsed = parseApiNumber(params[key])
      if (parsed !== null) {
        result[key] = parsed
      }
    }
  }
  if (params.cadence != null && params.cadence !== '') {
    result.cadence = String(params.cadence) as DriverParams['cadence']
  }
  if (params.anchor_date != null && params.anchor_date !== '') {
    result.anchor_date = String(params.anchor_date)
  }
  return Object.keys(result).length > 0 ? result : undefined
}

function toApiParams(params?: DriverParams): Record<string, unknown> {
  if (!params) {
    return {}
  }
  const out: Record<string, unknown> = {}
  if (params.ref) {
    out.ref = params.ref
  }
  for (const key of ['start', 'end', 'midpoint', 'steepness'] as const) {
    if (params[key] != null) {
      out[key] = String(params[key])
    }
  }
  if (params.cadence) {
    out.cadence = params.cadence
  }
  if (params.anchor_date) {
    out.anchor_date = params.anchor_date
  }
  return out
}

function parseApiDriverValues(values: (string | null)[] | undefined): (number | null)[] {
  const result: (number | null)[] = []
  for (let i = 0; i < 36; i += 1) {
    const raw = values?.[i] ?? null
    result.push(raw == null || raw === '' ? null : parseApiNumber(raw))
  }
  return result
}

function toApiDriverValues(values: (number | null)[]): (string | null)[] {
  return values.map((value) => (value === null ? null : String(value)))
}

function coerceRecipeNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = parseApiNumber(value)
    return parsed
  }
  return null
}

function parseApiRecipe(raw: unknown): Recipe | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }
  const obj = raw as Record<string, unknown>
  const anchorRaw = obj.anchor
  if (!anchorRaw || typeof anchorRaw !== 'object') {
    return null
  }
  const anchorObj = anchorRaw as Record<string, unknown>
  const anchorValue = coerceRecipeNumber(anchorObj.value)
  if (anchorValue === null) {
    return null
  }
  const recipe: Recipe = {
    anchor: {
      value: anchorValue,
      ...(anchorObj.basis != null ? { basis: anchorObj.basis as Recipe['anchor']['basis'] } : {}),
      ...(anchorObj.months != null ? { months: Number(anchorObj.months) as Recipe['anchor']['months'] } : {}),
      ...(anchorObj.runs != null ? { runs: Number(anchorObj.runs) as Recipe['anchor']['runs'] } : {}),
      ...(anchorObj.history_value != null
        ? { history_value: coerceRecipeNumber(anchorObj.history_value) ?? undefined }
        : {}),
    },
  }
  if (Array.isArray(obj.path)) {
    recipe.path = obj.path
      .filter((seg): seg is Record<string, unknown> => typeof seg === 'object' && seg !== null)
      .map((seg) => ({
        to: coerceRecipeNumber(seg.to) ?? 0,
        by: String(seg.by ?? ''),
        ...(seg.shape === 'step' || seg.shape === 'linear' ? { shape: seg.shape } : {}),
      }))
  }
  if (obj.growth && typeof obj.growth === 'object') {
    recipe.growth = obj.growth as Recipe['growth']
  }
  if (obj.seasonality && typeof obj.seasonality === 'object') {
    recipe.seasonality = obj.seasonality as Recipe['seasonality']
  }
  if (obj.overrides && typeof obj.overrides === 'object') {
    const overrides: Record<string, RecipeOverride> = {}
    for (const [month, val] of Object.entries(obj.overrides as Record<string, unknown>)) {
      if (typeof val === 'number' || typeof val === 'string') {
        const n = coerceRecipeNumber(val)
        if (n !== null) {
          overrides[month] = { value: n }
        }
      } else if (val && typeof val === 'object') {
        const v = val as Record<string, unknown>
        const n = coerceRecipeNumber(v.value)
        if (n !== null) {
          overrides[month] = {
            value: n,
            ...(typeof v.reason === 'string' ? { reason: v.reason } : {}),
          }
        }
      }
    }
    if (Object.keys(overrides).length > 0) {
      recipe.overrides = overrides
    }
  }
  if (typeof obj.rationale === 'string') {
    recipe.rationale = obj.rationale
  }
  return normaliseRecipe(recipe)
}

export function parseApiCashPolicySplitSchedule(
  schedule: ApiCashPolicySplitSchedule,
): CashPolicySplitSchedule {
  return {
    basis: {
      anchor_date: schedule.basis.anchor_date,
      cadence: schedule.basis.cadence,
      amount_per_period: parseApiNumber(schedule.basis.amount_per_period) ?? 0,
      horizon_months: schedule.basis.horizon_months,
      last_dist_num: schedule.basis.last_dist_num,
      last_closed_month: schedule.basis.last_closed_month ?? '',
      mode: schedule.basis.mode,
      one_time_date: schedule.basis.one_time_date ?? null,
      one_time_amount: parseApiNumber(schedule.basis.one_time_amount ?? null),
    },
    computed_at: schedule.computed_at,
    months: schedule.months.map((month) => ({
      t: month.t,
      lines: Object.fromEntries(
        Object.entries(month.lines).map(([key, value]) => [key, parseApiNumber(value) ?? 0]),
      ),
    })),
    periods: schedule.periods.map((period) => ({
      date: period.date,
      t: period.t,
      tiers: Object.fromEntries(
        Object.entries(period.tiers).map(([key, value]) => [key, parseApiNumber(value) ?? 0]),
      ),
    })),
    ...(Array.isArray(schedule.pref_accrual)
      ? {
          pref_accrual: schedule.pref_accrual.map((month) => ({
            t: month.t,
            lines: Object.fromEntries(
              Object.entries(month.lines).map(([key, value]) => [key, parseApiNumber(value) ?? 0]),
            ),
          })),
        }
      : {}),
  }
}

export function toApiCashPolicySplitSchedule(
  schedule: CashPolicySplitSchedule,
): ApiCashPolicySplitSchedule {
  return {
    basis: {
      anchor_date: schedule.basis.anchor_date,
      cadence: schedule.basis.cadence,
      amount_per_period: String(schedule.basis.amount_per_period),
      horizon_months: schedule.basis.horizon_months,
      last_dist_num: schedule.basis.last_dist_num,
      last_closed_month: schedule.basis.last_closed_month,
      mode: schedule.basis.mode,
      one_time_date: schedule.basis.one_time_date ?? null,
      one_time_amount:
        schedule.basis.one_time_amount === null || schedule.basis.one_time_amount === undefined
          ? null
          : String(schedule.basis.one_time_amount),
    },
    computed_at: schedule.computed_at,
    months: schedule.months.map((month) => ({
      t: month.t,
      lines: Object.fromEntries(
        Object.entries(month.lines).map(([key, value]) => [key, String(value)]),
      ),
    })),
    periods: schedule.periods.map((period) => ({
      date: period.date,
      t: period.t,
      tiers: Object.fromEntries(
        Object.entries(period.tiers).map(([key, value]) => [key, String(value)]),
      ),
    })),
    ...(Array.isArray(schedule.pref_accrual)
      ? {
          pref_accrual: schedule.pref_accrual.map((month) => ({
            t: month.t,
            lines: Object.fromEntries(
              Object.entries(month.lines).map(([key, value]) => [key, String(value)]),
            ),
          })),
        }
      : {}),
  }
}

function parseApiCashPolicy(apiPolicy: ApiCashPolicy | null | undefined): CashPolicy | null {
  if (!apiPolicy) {
    return null
  }
  const snapshot = apiPolicy.sweep.yield_snapshot
  return {
    enabled: apiPolicy.enabled,
    floor: {
      operating_minimum: parseApiNumber(apiPolicy.floor.operating_minimum),
      drawdown_allowance: parseApiNumber(apiPolicy.floor.drawdown_allowance),
    },
    distribution: {
      cadence: apiPolicy.distribution.cadence,
      anchor_date: apiPolicy.distribution.anchor_date,
      amount_per_period: parseApiNumber(apiPolicy.distribution.amount_per_period),
      ...(apiPolicy.distribution.one_time
        ? {
            one_time: {
              date: apiPolicy.distribution.one_time.date,
              amount: parseApiNumber(apiPolicy.distribution.one_time.amount) ?? 0,
            },
          }
        : {}),
      split_schedule: apiPolicy.distribution.split_schedule
        ? parseApiCashPolicySplitSchedule(apiPolicy.distribution.split_schedule)
        : null,
    },
    sweep: {
      enabled: apiPolicy.sweep.enabled,
      mode: apiPolicy.sweep.mode ?? 'excess',
      amount_per_period: parseApiNumber(apiPolicy.sweep.amount_per_period ?? null),
      yield_snapshot: snapshot
        ? {
            as_of: snapshot.as_of,
            source: snapshot.source,
            spread: parseApiNumber(snapshot.spread) ?? 0,
            apr_by_month: snapshot.apr_by_month.map((apr) => parseApiNumber(apr) ?? 0),
          }
        : null,
      yield_income_view: apiPolicy.sweep.yield_income_view,
      opening_mm_balance: parseApiNumber(apiPolicy.sweep.opening_mm_balance),
    },
    solver: apiPolicy.solver,
  }
}

function toApiCashPolicy(policy: CashPolicy | null): ApiCashPolicy | null {
  if (!policy) {
    return null
  }
  const snapshot = policy.sweep.yield_snapshot
  return {
    enabled: policy.enabled,
    floor: {
      operating_minimum:
        policy.floor.operating_minimum === null ? null : String(policy.floor.operating_minimum),
      drawdown_allowance:
        policy.floor.drawdown_allowance === null ? null : String(policy.floor.drawdown_allowance),
    },
    distribution: {
      cadence: policy.distribution.cadence,
      anchor_date: policy.distribution.anchor_date,
      amount_per_period:
        policy.distribution.amount_per_period === null
          ? null
          : String(policy.distribution.amount_per_period),
      ...(policy.distribution.one_time &&
      policy.distribution.one_time.date &&
      policy.distribution.one_time.amount
        ? {
            one_time: {
              date: policy.distribution.one_time.date,
              amount: String(policy.distribution.one_time.amount),
            },
          }
        : {}),
      split_schedule: policy.distribution.split_schedule
        ? toApiCashPolicySplitSchedule(policy.distribution.split_schedule)
        : null,
    },
    sweep: {
      enabled: policy.sweep.enabled,
      mode: policy.sweep.mode ?? 'excess',
      amount_per_period:
        policy.sweep.amount_per_period === null
          ? null
          : String(policy.sweep.amount_per_period),
      yield_snapshot: snapshot
        ? {
            as_of: snapshot.as_of,
            source: snapshot.source,
            spread: String(snapshot.spread),
            apr_by_month: snapshot.apr_by_month.map((apr) => String(apr)),
          }
        : null,
      yield_income_view: policy.sweep.yield_income_view,
      opening_mm_balance:
        policy.sweep.opening_mm_balance === null
          ? null
          : String(policy.sweep.opening_mm_balance),
    },
    solver: policy.solver,
  }
}

export function toEngineInputs(apiInputs: ApiCaseInputs): EngineCaseInputs {
  const drivers: EngineCaseInputs['drivers'] = {}
  for (const [view, lines] of Object.entries(apiInputs.drivers ?? {})) {
    drivers[view] = {}
    for (const [lineKey, driverInputs] of Object.entries(lines)) {
      const byType: Record<string, { values: (number | null)[]; params?: DriverParams }> = {}
      for (const [driverType, cache] of Object.entries(driverInputs.by_type ?? {})) {
        const recipe = cache.recipe != null ? parseApiRecipe(cache.recipe) : null
        byType[driverType] = {
          values: parseApiDriverValues(cache.values),
          params: parseApiParams(cache.params),
          ...(recipe ? { recipe } : {}),
        }
      }
      drivers[view]![lineKey] = {
        active: (driverInputs.active as DriverType | null) ?? null,
        by_type: byType,
      }
    }
  }

  return migrateTotalOrdersDriversToAvgOrders({
    consolidated_only: [...(apiInputs.consolidated_only ?? [])],
    drivers,
    one_time_items: (apiInputs.one_time_items ?? []).map((item) => ({
      id: item.id,
      month: item.month,
      view: item.view,
      line_key: item.line_key,
      amount: parseApiNumber(item.amount) ?? 0,
      mode: item.mode,
      notes: item.notes ?? undefined,
    })),
    cash_policy: parseApiCashPolicy(apiInputs.cash_policy),
  })
}

export function toApiInputs(engineInputs: EngineCaseInputs): ApiCaseInputs {
  const drivers: ApiCaseInputs['drivers'] = {}
  for (const [view, lines] of Object.entries(engineInputs.drivers ?? {})) {
    drivers[view] = {}
    for (const [lineKey, driverInputs] of Object.entries(lines)) {
      const byType: Record<string, ApiDriverTypeCache> = {}
      for (const [driverType, cache] of Object.entries(driverInputs.by_type ?? {})) {
        byType[driverType] = {
          values: toApiDriverValues(cache.values ?? Array(36).fill(null)),
          params: toApiParams(cache.params),
          ...(cache.recipe ? { recipe: normaliseRecipe(cache.recipe) } : {}),
        }
      }
      drivers[view]![lineKey] = {
        active: driverInputs.active,
        by_type: byType,
      }
    }
  }

  return {
    consolidated_only: [...engineInputs.consolidated_only],
    drivers,
    one_time_items: engineInputs.one_time_items.map((item) => ({
      id: item.id,
      month: item.month,
      view: item.view,
      line_key: item.line_key,
      amount: String(item.amount),
      mode: item.mode,
      notes: item.notes ?? null,
    })),
    cash_policy: toApiCashPolicy(engineInputs.cash_policy),
  }
}

export function toApiOutputs(evaluated: EngineModel): Record<string, Record<string, (string | null)[]>> {
  const outputs: Record<string, Record<string, (string | null)[]>> = {}
  for (const [view, lines] of Object.entries(evaluated.values)) {
    outputs[view] = {}
    for (const [lineKey, cells] of Object.entries(lines)) {
      const values: (string | null)[] = []
      for (let t = 1; t <= 36; t += 1) {
        const value = cells[t] ?? null
        values.push(value === null ? null : String(value))
      }
      outputs[view]![lineKey] = values
    }
  }
  return outputs
}

export function toApiKpis(evaluated: EngineModel): Record<string, unknown> {
  const stringify = (value: number | null) => (value === null ? null : String(value))
  const summary = evaluated.cash_policy?.summary
  return {
    net_income: {
      t3: stringify(evaluated.kpis.net_income.t3),
      t6: stringify(evaluated.kpis.net_income.t6),
      t12: stringify(evaluated.kpis.net_income.t12),
    },
    cash: {
      t3: stringify(evaluated.kpis.cash.t3),
      t6: stringify(evaluated.kpis.cash.t6),
      t12: stringify(evaluated.kpis.cash.t12),
    },
    total_cash: {
      t3: stringify(evaluated.kpis.total_cash.t3),
      t6: stringify(evaluated.kpis.total_cash.t6),
      t12: stringify(evaluated.kpis.total_cash.t12),
    },
    cash_policy_summary: summary
      ? {
          min_operating_cash: String(summary.min_operating_cash),
          min_operating_cash_t: summary.min_operating_cash_t,
          breach_months: summary.breach_months,
          max_shortfall: String(summary.max_shortfall),
          distributions_12m: String(summary.distributions_12m),
          sweep_12m: String(summary.sweep_12m),
          interest_12m: String(summary.interest_12m),
          mm_balance_t12: String(summary.mm_balance_t12),
          mm_balance_t24: String(summary.mm_balance_t24),
          mm_balance_t36: String(summary.mm_balance_t36),
          split_source: summary.split_source,
        }
      : null,
    cash_policy_chart: evaluated.cash_policy?.months.map((month) => ({
      month: month.month,
      operating_cash: String(month.operating_cash),
      mm_balance: String(month.mm_balance),
      distributions: String(Math.abs(month.distributions)),
    })) ?? null,
  }
}

function getErrorDetail(error: unknown): Record<string, unknown> | null {
  const detail = getApiErrorDetail(error)
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as Record<string, unknown>
  }
  return null
}

function isAxiosStatus(error: unknown, status: number): boolean {
  return (
    error != null &&
    typeof error === 'object' &&
    'response' in error &&
    (error as AxiosError).response?.status === status
  )
}

export function isImportBlocked(
  error: unknown,
): { control_status: string; detail: string } | null {
  if (!isAxiosStatus(error, 409)) {
    return null
  }
  const detail = getErrorDetail(error)
  if (!detail || detail.blocking !== true) {
    return null
  }
  return {
    control_status: String(detail.control_status ?? ''),
    detail: String(detail.detail ?? ''),
  }
}

export function isImportDrift(error: unknown): { diff: unknown } | null {
  if (!isAxiosStatus(error, 409)) {
    return null
  }
  const detail = getErrorDetail(error)
  if (!detail || detail.blocking === true || !('diff' in detail)) {
    return null
  }
  return { diff: detail.diff }
}

export function isConcurrencyConflict(error: unknown): { case: CaseDetail } | null {
  if (!isAxiosStatus(error, 409)) {
    return null
  }
  const detail = getErrorDetail(error)
  if (!detail || detail.code !== 'concurrency' || !detail.case) {
    return null
  }
  return { case: detail.case as CaseDetail }
}

export function isValidationError(
  error: unknown,
): { errors: ValidationItem[]; warnings: ValidationItem[] } | null {
  if (!isAxiosStatus(error, 422)) {
    return null
  }
  const rawDetail = getApiErrorDetail(error)
  const detail = getErrorDetail(error)
  if (detail && Array.isArray(detail.errors)) {
    return {
      errors: detail.errors as ValidationItem[],
      warnings: (detail.warnings as ValidationItem[]) ?? [],
    }
  }
  if (Array.isArray(rawDetail)) {
    const errors: ValidationItem[] = rawDetail.map((item, index) => {
      if (item && typeof item === 'object' && 'msg' in item) {
        const loc = 'loc' in item && Array.isArray(item.loc) ? item.loc.join('.') : 'request'
        return {
          code: 'validation_error',
          message: String(item.msg),
          line_key: loc,
          view: null,
          t: null,
          label: null,
          section: null,
        }
      }
      return {
        code: 'validation_error',
        message: JSON.stringify(item),
        line_key: `item_${index}`,
        view: null,
        t: null,
        label: null,
        section: null,
      }
    })
    return { errors, warnings: [] }
  }
  return null
}

export { formatApiError }

export type FloorAnalysisMonth = {
  report_month: string
  statement_start: string
  statement_end: string
  beginning_balance: string
  ending_balance: string
  min_balance: string
  min_balance_date: string
  drawdown: string
  total_outflows: string
  outflow_days: number
}

export type FloorAnalysisResponse = {
  account_key: string
  months: FloorAnalysisMonth[]
  stats: {
    max_drawdown: string
    p90_drawdown: string
    mean_drawdown: string
    avg_daily_outflow: string
    avg_monthly_outflow: string
  }
  recommended: {
    drawdown_allowance: string
    cushion_days: number
    cushion: string
    floor_hint: string
  }
  missing_months: string[]
  excluded_distribution_count: number
  excluded_distributions: string[]
  unmatched_distribution_jes: string[]
}

export function normalizeFloorAnalysisResponse(
  data: FloorAnalysisResponse,
): FloorAnalysisResponse {
  return {
    ...data,
    excluded_distributions: data.excluded_distributions ?? [],
  }
}

export type DistributionHistoryItem = {
  num: number
  date: string
  amount: string
}

export type DistributionHistoryResponse = {
  items: DistributionHistoryItem[]
  last_date: string | null
  median_gap_days: number | null
  avg_amount_last_6: string | null
}

export type WaterfallScheduleRequest = {
  anchor_date: string
  cadence: CashPolicyCadence
  amount_per_period: string
  horizon_months: number
  mode?: WaterfallMode
  one_time_date?: string | null
  one_time_amount?: string | null
}

export function waterfallScheduleRequestFromPolicy(policy: CashPolicy): WaterfallScheduleRequest {
  const oneTime = policy.distribution.one_time
  const hasExtra =
    oneTime &&
    oneTime.date &&
    oneTime.date.trim() !== '' &&
    oneTime.amount != null &&
    oneTime.amount !== 0
  return {
    anchor_date: policy.distribution.anchor_date,
    cadence: policy.distribution.cadence,
    amount_per_period: String(policy.distribution.amount_per_period ?? 0),
    horizon_months: 36,
    ...(hasExtra
      ? { one_time_date: oneTime.date, one_time_amount: String(oneTime.amount) }
      : {}),
  }
}

export type WaterfallScheduleResponse = {
  split_schedule: ApiCashPolicySplitSchedule
  warnings: string[]
  elapsed_ms: number
  last_dist_date?: string | null
}

type FlatWaterfallSchedulePayload = ApiCashPolicySplitSchedule & {
  warnings?: string[]
  elapsed_ms?: number
  last_dist_date?: string | null
}

export function normalizeWaterfallScheduleResponse(
  data: WaterfallScheduleResponse | FlatWaterfallSchedulePayload,
): WaterfallScheduleResponse {
  if (data && 'split_schedule' in data && data.split_schedule != null) {
    return {
      split_schedule: data.split_schedule,
      warnings: data.warnings ?? [],
      elapsed_ms: data.elapsed_ms,
      last_dist_date: data.last_dist_date ?? null,
    }
  }
  const {
    warnings = [],
    elapsed_ms = 0,
    last_dist_date = null,
    ...schedule
  } = data as FlatWaterfallSchedulePayload
  return {
    split_schedule: schedule,
    warnings,
    elapsed_ms,
    last_dist_date,
  }
}

export type YieldCurveResponse = {
  as_of: string
  source: string
  spread: string
  apr_by_month: string[]
  par: Record<string, string>
  stale: boolean
}

export async function getModel(): Promise<ModelSummaryResponse> {
  const { data } = await api.get<ModelSummaryResponse>('/projections/model')
  return data
}

export async function getFloorAnalysis(months = 12): Promise<FloorAnalysisResponse> {
  const { data } = await api.get<FloorAnalysisResponse>('/projections/cash-policy/floor-analysis', {
    params: { months },
  })
  return normalizeFloorAnalysisResponse(data)
}

export async function getDistributionHistory(
  limit = 12,
): Promise<DistributionHistoryResponse> {
  const { data } = await api.get<DistributionHistoryResponse>(
    '/projections/cash-policy/distribution-history',
    { params: { limit } },
  )
  return data
}

export async function computeWaterfallSchedule(
  body: WaterfallScheduleRequest,
): Promise<WaterfallScheduleResponse> {
  const { data } = await api.post<WaterfallScheduleResponse | FlatWaterfallSchedulePayload>(
    '/projections/cash-policy/waterfall-schedule',
    body,
  )
  return normalizeWaterfallScheduleResponse(data)
}

export async function getYieldCurve(): Promise<YieldCurveResponse> {
  const { data } = await api.get<YieldCurveResponse>('/projections/cash-policy/yield-curve')
  return data
}

export async function getHistory(): Promise<CgmHistoryPayload> {
  const { data } = await api.get<CgmHistoryPayload>('/projections/history')
  return data
}

export async function importModel(): Promise<ImportResultResponse> {
  const { data } = await api.post<ImportResultResponse>('/projections/import')
  return data
}

export async function revertLatestImport(): Promise<{
  previous_import_id: number
  last_closed_month: string
  cases_restored: number
}> {
  const { data } = await api.post<{
    previous_import_id: number
    last_closed_month: string
    cases_restored: number
  }>('/projections/import/revert')
  return data
}

export async function rollForwardCases(caseIds?: number[]): Promise<RollForwardSummary> {
  const { data } = await api.post<RollForwardSummary>(
    '/projections/roll-forward',
    caseIds != null ? { case_ids: caseIds } : {},
  )
  return data
}

export type RollForwardRun = {
  id: number
  import_id: number
  started_at: string
  finished_at: string | null
  triggered_by: string | null
  k_months: number
  summary: RollForwardSummary
}

export async function listRollForwardRuns(limit = 5): Promise<RollForwardRun[]> {
  const { data } = await api.get<RollForwardRun[]>('/projections/roll-forward/runs', {
    params: { limit },
  })
  return data
}

export async function importLaborHistory(): Promise<LaborHistoryImportResponse> {
  const { data } = await api.post<LaborHistoryImportResponse>(
    '/projections/labor-history/import',
  )
  return data
}

export async function listCases(): Promise<CaseListItem[]> {
  const { data } = await api.get<CaseListItem[]>('/projections/cases')
  return data
}

export async function createCase(body: CaseCreateBody): Promise<CaseDetail> {
  const { data } = await api.post<CaseDetail>('/projections/cases', body)
  return data
}

export type SnapshotDriverInput = {
  active: string | null
  value: string | null
  params: Record<string, unknown> | null
}

export type SnapshotRow = {
  month: string
  t_at_capture: number
  captured_at: string | null
  case_saved_at: string | null
  from_import_id?: number
  to_import_id?: number
  values?: Record<string, Record<string, string>>
  driver_inputs?: Record<string, Record<string, SnapshotDriverInput>>
  one_time_items?: unknown[]
}

export async function listCaseSnapshots(
  caseId: number,
  includeValues = false,
): Promise<SnapshotRow[]> {
  const { data } = await api.get<SnapshotRow[]>(`/projections/cases/${caseId}/snapshots`, {
    params: includeValues ? { include: 'values' } : undefined,
  })
  return data
}

export async function getCase(id: number): Promise<CaseDetail> {
  const { data } = await api.get<CaseDetail>(`/projections/cases/${id}`)
  return data
}

export async function updateCase(id: number, body: CaseUpdateBody): Promise<CaseDetail> {
  const { data } = await api.put<CaseDetail>(`/projections/cases/${id}`, body)
  return data
}

export async function deleteCase(id: number): Promise<void> {
  await api.delete(`/projections/cases/${id}`)
}

export async function getNotes(caseId: number): Promise<Record<string, string>> {
  const { data } = await api.get<Record<string, string>>(`/projections/cases/${caseId}/notes`)
  return data
}

export async function getSharedNotes(): Promise<Record<string, string>> {
  const { data } = await api.get<Record<string, string>>('/projections/shared-notes')
  return data
}

export async function upsertNote(caseId: number, cellKey: string, note: string): Promise<void> {
  await api.put(`/projections/cases/${caseId}/notes/${cellKey}`, { note })
}

export async function upsertSharedNote(cellKey: string, note: string): Promise<void> {
  await api.put(`/projections/shared-notes/${cellKey}`, { note })
}

export type ExportRange = {
  tab: string
  range: string
  first_col: string
  last_col: string
  first_row: number
  last_row: number
}

export type ExportNonEmptyCell = {
  tab: string
  range: string
  cells: { row_offset: number; col_offset: number; value: unknown }[]
}

export type ExportDryRunResponse = {
  ranges: ExportRange[]
  non_empty: ExportNonEmptyCell[]
  first_col: string | null
}

export type ProjectionExportListItem = {
  id: number
  case_id: number
  import_id: number
  exported_at: string
  exported_by: string
  last_closed_month: string | null
  first_col: string | null
  ranges: ExportRange[] | null
  status: string
  error: string | null
}

export type SheetLayoutDriftItem = {
  tab?: string
  row?: number
  expected?: string
  actual?: string
}

export type ExportConflictCode =
  | 'dirty'
  | 'expired'
  | 'stale_import'
  | 'control_moved'
  | 'control_blocking'
  | 'schema_stale'
  | 'sheet_layout_drift'
  | 'outputs_stale'

export async function exportCase(
  caseId: number,
  options: { dry_run: boolean; expected_updated_at: string },
): Promise<ExportDryRunResponse> {
  const { data } = await api.post<ExportDryRunResponse>(
    `/projections/cases/${caseId}/export`,
    { expected_updated_at: options.expected_updated_at, dry_run: options.dry_run },
  )
  return data
}

export async function listExports(caseId?: number): Promise<ProjectionExportListItem[]> {
  const { data } = await api.get<ProjectionExportListItem[]>('/projections/exports', {
    params: caseId != null ? { case_id: caseId } : undefined,
  })
  return data
}

const EXPORT_CONFLICT_CODES = new Set<ExportConflictCode>([
  'dirty',
  'expired',
  'stale_import',
  'control_moved',
  'control_blocking',
  'schema_stale',
  'sheet_layout_drift',
  'outputs_stale',
])

export function isExportConflict(
  error: unknown,
): { code: ExportConflictCode; detail: string; diff?: SheetLayoutDriftItem[] } | null {
  if (!isAxiosStatus(error, 409)) {
    return null
  }
  const detail = getErrorDetail(error)
  if (!detail) {
    return null
  }
  const code = detail.code
  if (typeof code === 'string' && EXPORT_CONFLICT_CODES.has(code as ExportConflictCode)) {
    const diffRaw = detail.diff
    const diff = Array.isArray(diffRaw)
      ? (diffRaw as SheetLayoutDriftItem[])
      : undefined
    return {
      code: code as ExportConflictCode,
      detail: String(detail.detail ?? ''),
      diff,
    }
  }
  return null
}

export type SummaryMeta = {
  id: number
  generated_at: string
  generated_by: string
  case_saved_at: string
  prose_source: 'llm' | 'fallback'
  grounding_failures: string[]
  model: string | null
  page_count: number
  truncated: boolean
  html: string
}

export type CaseSummaryResponse = { summary: SummaryMeta | null; stale: boolean }

export type SummaryConflictCode = 'case_dirty_or_unsaved' | 'case_expired'

const SUMMARY_CONFLICT_CODES = new Set<SummaryConflictCode>([
  'case_dirty_or_unsaved',
  'case_expired',
])

export async function getCaseSummary(caseId: number): Promise<CaseSummaryResponse> {
  const { data } = await api.get<CaseSummaryResponse>(`/projections/cases/${caseId}/summary`)
  return data
}

export async function generateCaseSummary(caseId: number, force: boolean): Promise<SummaryMeta> {
  const { data } = await api.post<SummaryMeta>(`/projections/cases/${caseId}/summary`, { force })
  return data
}

export async function refreshCaseSummary(caseId: number): Promise<SummaryMeta> {
  const { data } = await api.post<SummaryMeta>(`/projections/cases/${caseId}/summary`, {
    refresh: true,
  })
  return data
}

export async function downloadCaseSummaryPdf(caseId: number): Promise<Blob | File> {
  const response = await api.get<Blob>(`/projections/cases/${caseId}/summary/pdf`, {
    responseType: 'blob',
  })
  const blob = response.data
  const disposition = response.headers['content-disposition'] as string | undefined
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition)
    if (match?.[1]) {
      return new File([blob], match[1], { type: blob.type || 'application/pdf' })
    }
  }
  return blob
}

export async function downloadCaseAssumptionsPdf(caseId: number): Promise<Blob | File> {
  const response = await api.get<Blob>(
    `/projections/cases/${caseId}/summary/assumptions.pdf`,
    {
      responseType: 'blob',
    },
  )
  const blob = response.data
  const disposition = response.headers['content-disposition'] as string | undefined
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition)
    if (match?.[1]) {
      return new File([blob], match[1], { type: blob.type || 'application/pdf' })
    }
  }
  return blob
}

export function isSummaryConflict(
  error: unknown,
): { code: SummaryConflictCode; detail: string } | null {
  if (!isAxiosStatus(error, 409)) {
    return null
  }
  const detail = getErrorDetail(error)
  if (!detail) {
    return null
  }
  const code = detail.code
  if (typeof code === 'string' && SUMMARY_CONFLICT_CODES.has(code as SummaryConflictCode)) {
    return {
      code: code as SummaryConflictCode,
      detail: String(detail.detail ?? ''),
    }
  }
  return null
}
